/**
 * Seeded layout generation (GEN-001).
 *
 * Lives in `sim`, alongside the physics, for the same reason the physics is there:
 * the seed → layout mapping has to give **identical** results in the browser (the
 * Phase 1 layout lab) and in `packages/levelgen` (bulk generation offline), and those
 * two cannot import each other. A level that regenerated differently on the server
 * than it did in a player's browser would be a level nobody could verify.
 *
 * Determinism rules apply in full: seeded PRNG only, no `Math.random`, and no
 * transcendentals (ARCHITECTURE.md §5.5).
 */
import { DEFAULT_PHYSICS, type PhysicsConfig } from './config.ts';
import { createRng } from './random.ts';
import { createTableGeometry, createTableSpec, type TableSizeFt } from './table.ts';
import type { Layout } from './types.ts';

export interface LayoutOptions {
  readonly sizeFt: TableSizeFt;
  /** 3–10 object balls, plus the cue ball (brief §4). */
  readonly ballCount: number;
}

export interface LayoutConstraints {
  /** Centre-to-centre, as a multiple of R. Above 2 so balls never touch. */
  readonly minSeparationR: number;
  /**
   * Cue ball to every object ball, centre to centre, as a multiple of R. The owner's
   * report (GEN-007): a cue ball dealt almost touching an object ball is a poor, cramped
   * first shot. 8R leaves about three ball widths of cloth between them.
   */
  readonly cueClearanceR: number;
  /** Clear space from a cushion nose, as a multiple of R. */
  readonly cushionMarginR: number;
  /** Clear space from a pocket throat, as a multiple of R, so nothing starts potted. */
  readonly pocketMarginR: number;
  /** Give up and reseed after this many failed placements. */
  readonly maxAttempts: number;
}

export const DEFAULT_CONSTRAINTS: LayoutConstraints = {
  minSeparationR: 2.1,
  cueClearanceR: 8,
  cushionMarginR: 2,
  pocketMarginR: 2,
  maxAttempts: 4000,
};

/**
 * Object balls per weekday: easier at the start of the week, hardest at the weekend
 * (brief §8). Indexed Sunday = 0, so the two weekend days sit at the ends.
 *
 * A table rather than a formula, because it is meant to be tuned by feel.
 *
 * 4 to 8 balls on every day (owner, 2026-09-27, D-046): three felt too few, and nine
 * or ten too many. The same shape as before, squeezed into that range.
 *
 * At least 5 (owner, 2026-09-28, D-052): four balls felt too few, above all on a 9ft
 * table. Only the 4-ball days changed: Monday is 5, Tuesday 5 to 6.
 */
export const DEFAULT_DIFFICULTY_CURVE: readonly (readonly [number, number])[] = [
  [7, 8], // Sunday
  [5, 5], // Monday
  [5, 6], // Tuesday
  [5, 7], // Wednesday
  [5, 8], // Thursday
  [6, 8], // Friday
  [7, 8], // Saturday
];

/**
 * Day of the week for a `YYYY-MM-DD` key, Sunday = 0.
 *
 * Computed with Zeller's congruence rather than `Date`, because `sim` has no clock
 * (PRD N2) — and because a date string should not depend on the host's timezone to
 * become a weekday.
 */
export function dayOfWeek(dateKey: string): number {
  const year = Number(dateKey.slice(0, 4));
  const month = Number(dateKey.slice(5, 7));
  const day = Number(dateKey.slice(8, 10));

  // Zeller treats January and February as months 13 and 14 of the previous year.
  const m = month < 3 ? month + 12 : month;
  const y = month < 3 ? year - 1 : year;
  const k = y % 100;
  const j = Math.floor(y / 100);

  const h =
    (day + Math.floor((13 * (m + 1)) / 5) + k + Math.floor(k / 4) + Math.floor(j / 4) + 5 * j) % 7;
  // Zeller gives Saturday = 0; shift so Sunday = 0.
  return (h + 6) % 7;
}

/** Squared distance from point (px,py) to the segment a→b. */
function distanceSquaredToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx - px;
  const cy = ay + t * dy - py;
  return cx * cx + cy * cy;
}

/** Can a ball travel from a→b without another ball being in the way? */
function pathIsClear(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  balls: readonly { xM: number; yM: number }[],
  skip: readonly number[],
  diameterM: number,
): boolean {
  for (let i = 0; i < balls.length; i++) {
    if (skip.includes(i)) continue;
    const ball = balls[i];
    if (ball === undefined) continue;
    if (distanceSquaredToSegment(ball.xM, ball.yM, ax, ay, bx, by) < diameterM * diameterM) {
      return false;
    }
  }
  return true;
}

/**
 * Is this layout actually playable?
 *
 * A cheap stand-in for the solver, which does not exist until Phase 2. It rejects the
 * layouts a player would call unfair rather than hard: one where the cue ball cannot
 * see a single object ball, or one where some object ball has no clear line to any
 * pocket. It does NOT claim the layout is solvable — only the solver can say that, and
 * it will reject what it cannot clear (brief §9).
 */
export function isPlayable(layout: Layout, sizeFt: TableSizeFt, config = DEFAULT_PHYSICS): boolean {
  const geometry = createTableGeometry(createTableSpec(sizeFt, config.pocketScale));
  const all = [layout.cue, ...layout.balls];
  const diameterM = config.ballRadiusM * 2;

  const cueSeesSomething = layout.balls.some((ball, index) =>
    pathIsClear(layout.cue.xM, layout.cue.yM, ball.xM, ball.yM, all, [0, index + 1], diameterM),
  );
  if (!cueSeesSomething) return false;

  return layout.balls.every((ball, index) =>
    geometry.pockets.some((pocket) =>
      pathIsClear(
        ball.xM,
        ball.yM,
        pocket.centre.xM,
        pocket.centre.yM,
        all,
        [index + 1],
        diameterM,
      ),
    ),
  );
}

/**
 * Generate a layout from a seed. The same seed and `generatorVersion` always give the
 * same table (brief §8).
 */
export function generateLayout(
  seed: string,
  options: LayoutOptions,
  config: PhysicsConfig = DEFAULT_PHYSICS,
  constraints: LayoutConstraints = DEFAULT_CONSTRAINTS,
): Layout {
  const spec = createTableSpec(options.sizeFt, config.pocketScale);
  const geometry = createTableGeometry(spec);
  const R = config.ballRadiusM;

  const minSeparation = constraints.minSeparationR * R;
  const cueClearance = Math.max(minSeparation, constraints.cueClearanceR * R);
  const margin = constraints.cushionMarginR * R;
  const pocketMargin = constraints.pocketMarginR * R;

  // Reseeded on each retry, so a rejected attempt does not simply repeat.
  for (let attempt = 0; attempt < 32; attempt++) {
    const rng = createRng(`${seed}:layout:${attempt}`);
    const placed: { xM: number; yM: number }[] = [];
    let tries = 0;

    while (placed.length < options.ballCount + 1 && tries < constraints.maxAttempts) {
      tries++;
      const candidate = {
        xM: rng.range(margin, spec.widthM - margin),
        yM: rng.range(margin, spec.heightM - margin),
      };

      // Clear of every ball already down. The cue ball is placed first, so every
      // object ball after it keeps the wider cue clearance from it.
      let ok = true;
      for (let j = 0; j < placed.length; j++) {
        const other = placed[j];
        if (other === undefined) continue;
        const dx = other.xM - candidate.xM;
        const dy = other.yM - candidate.yM;
        if (Math.sqrt(dx * dx + dy * dy) < (j === 0 ? cueClearance : minSeparation)) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;

      // Clear of every pocket, so nothing begins the puzzle already down one.
      for (const pocket of geometry.pockets) {
        const dx = pocket.centre.xM - candidate.xM;
        const dy = pocket.centre.yM - candidate.yM;
        if (Math.sqrt(dx * dx + dy * dy) < pocket.throatRadiusM + pocketMargin) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;

      placed.push(candidate);
    }

    if (placed.length < options.ballCount + 1) continue;

    const [cue, ...balls] = placed;
    if (cue === undefined) continue;
    const layout: Layout = { cue, balls };
    if (isPlayable(layout, options.sizeFt, config)) return layout;
  }

  throw new Error(
    `generateLayout: could not place ${options.ballCount} balls on a ${options.sizeFt}ft table for seed "${seed}"`,
  );
}
